import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Navigate } from "react-router-dom";
import fuzzysort from "fuzzysort";
import { CreateCompanyModalStyled } from "./create-company-modal.style";
import CompanyInfoForm, {
  Company_INFO_FORM_VIEW_TYPES,
} from "../companies-info-form";
import { companiesCollection } from "../../../services/firebase";
import { getNextCompanyId } from "../../../services/countersService";
import { useAuth } from "../../../context/AuthContext";
import { Typography } from "@mui/material";
import { CreateCompanyValidations } from "../../../constants/validations";
import { yupResolver } from "@hookform/resolvers/yup";

const CreateCompanyModal = (props) => {
  const { onClose, nextId, defaultValues = {} } = props;
  const { userDetails } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [redirectPath, setRedirectPath] = useState(null);

  const form = useForm({
    mode: "all",
    defaultValues: {
      id: nextId?.toString(),
      companyName: "",
      type: "",
      companyState: "",
      streetAddress: "",
      address: "",
      city: "",
      province: "",
      postalCode: "",
      country: "",
      webAddress: "",
      phoneNumber: "",
      companyNotes: "",
      rateCard: "",
      isNotBilling: true,
      billingStreetAddress: "",
      billingAddress: "",
      billingCity: "",
      billingProvince: "",
      billingPostalCode: "",
      billingCountry: "",
      ...defaultValues,
    },

    resolver: yupResolver(CreateCompanyValidations()),
  });

  const { formState, getValues } = form;
  const { isValid } = formState;

  useEffect(() => {
    form.reset({
      ...form.getValues(),
      id: nextId ? nextId.toString() : "",
    });
  }, [nextId, form]);

  const handleSaveModal = async (formData) => {
    console.log("Received form data:", formData);
    setIsLoading(true);

    try {
      // Step 1: Fetch all existing company names
      const snapshot = await companiesCollection.get();
      const existingCompanyNames = snapshot.docs.map((doc) =>
        doc.data().companyName.toLowerCase()
      );

      const enteredCompanyName = formData.companyName.toLowerCase();

      // Step 2: Exact Match Check
      if (existingCompanyNames.includes(enteredCompanyName)) {
        alert(
          "Unable to create company. Another company with the same name already exists."
        );
        setIsLoading(false);
        return;
      }

      // Step 3: Fuzzy Matching for Similar Names
      const similarMatches = fuzzysort.go(
        enteredCompanyName,
        existingCompanyNames,
        { threshold: -100 }
      );

      if (similarMatches.length > 0) {
        const confirmed = window.confirm(
          "Are you sure? Another company with a similar name already exists. Press OK to proceed, or Cancel to go back."
        );
        if (!confirmed) {
          setIsLoading(false);
          return;
        }
      }

      // Step 4: Proceed with company creation
      const currentDate = new Date();
      const formattedDate = `${currentDate.getDate()}/${
        currentDate.getMonth() + 1
      }/${currentDate.getFullYear()}`;
      formData.createdBy = userDetails?.fullName;
      formData.dateCreated = formattedDate;

      const companyId = await getNextCompanyId();
      const companyData = {
        id: companyId,
        companyID: companyId.toString(),
        companyName: formData.companyName,
        type: formData.type,
        rateCard: formData.rateCard,
        companyState: formData.companyState,
        streetAddress: formData.streetAddress,
        address: formData.address,
        city: formData.city,
        province: formData.province,
        postalCode: formData.postalCode,
        country: formData.country,
        webAddress: formData.webAddress,
        phoneNumber: formData.phoneNumber,
        companyNotes: formData.companyNotes,
        isNotBilling: formData.isNotBilling,
        billingStreetAddress: formData.billingStreetAddress,
        billingAddress: formData.billingAddress,
        billingCity: formData.billingCity,
        billingProvince: formData.billingProvince,
        billingPostalCode: formData.billingPostalCode,
        billingCountry: formData.billingCountry,
        createdBy: formData.createdBy,
        dateCreated: formattedDate,
      };

      const companyRef = companiesCollection.doc(companyId.toString());
      await companyRef.set(companyData);

      console.log("Company saved successfully:", companyData);
      onSuccess(companyData);
    } catch (error) {
      console.error("Error saving company:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    const formData = getValues();
    handleSaveModal(formData);
  };

  const onSuccess = (companyData) => {
    setRedirectPath(`/companiesview/${companyData.id}/details`);
    onClose(); // Close modal
  };

  if (redirectPath) {
    return <Navigate to={redirectPath} />;
  }

  return (
    <CreateCompanyModalStyled
      confirmBtnTitle="Create Company"
      confirmBtnColor="#77E250"
      closeBtnTitle="Cancel"
      isConfirming={isLoading}
      disableConfirmBtn={!isValid}
      onConfirm={handleSubmit}
      onClose={onClose}
    >
      <div>
        <div className="head">
          <Typography variant="h4">Create Company</Typography>
        </div>
        <hr className="hr"></hr>
        <CompanyInfoForm
          form={form}
          view={Company_INFO_FORM_VIEW_TYPES.creation}
        />
      </div>
    </CreateCompanyModalStyled>
  );
};

export default CreateCompanyModal;
